<template>
  <div class="flex flex-col p-6">
    <div class="flex mb-5">
      <span class="text-sm font-medium flex items-center">
        {{ translate('timePeriod') }}
      </span>
      <div class="last-n-days-button flex items-center">
        <ListDropdownWrap
          ref="dropdown"
          :dropdown-width="140"
          :height="16"
          :items="dropdownItems"
          :selected-item="selectedItemIndex"
          :disable-scroll="true"
          @item-click="handleItemClick"
        />
      </div>
    </div>
    <div class="flex flex-col gap-2 text-sm font-normal">
      <div class="added flex items-center">
        {{ translate('cardsAdded') }}
        <span class="ml-[43px]"> {{ addedTasks }} </span>
        <em
          v-tooltip="{
            content: tasksAddedTooltip,
            placement: 'top',
          }"
          class="custom-icons w-4 h-4 ml-2 cursor-default bg-icon-default"
          :style="getCustomIconStyle(iconPath('info'))"
        />
      </div>
      <div class="finished flex items-center">
        {{ translate('cardsFinished') }}
        <span class="ml-8"> {{ finishedTasks }} </span>
        <em
          v-tooltip="{
            content: tasksFinishedTooltip,
            placement: 'top',
          }"
          class="custom-icons w-4 h-4 ml-2 cursor-default bg-icon-default"
          :style="getCustomIconStyle(iconPath('info'))"
        />
      </div>
      <div class="net-change flex items-center">
        {{ translate('netChange') }}
        <span class="ml-[50px]"> {{ netChange }} </span>
        <em
          v-tooltip="{
            content: netChangeTooltip,
            placement: 'top',
          }"
          class="custom-icons w-4 h-4 ml-2 cursor-default"
          :class="{
            'bg-icon-positiveIcon': netChange < 0 || netChange === 0,
            'bg-destructive-icon': netChange > 0,
          }"
          :style="getCustomIconStyle(iconPath('info'))"
        />
      </div>
    </div>
  </div>
</template>
<script>
import ListDropdownWrap from '@/components/widgets/ListDropdownWrap.vue';
import customIconMixin from '@/mixins/customIconsMixin';
import reportingMixin from '@/mixins/reportingMixin';
import { convertTimestampToDate } from '@/utilities/timeDifference';

export default {
  name: 'Reporting',
  components: {
    ListDropdownWrap,
  },
  mixins: [customIconMixin, reportingMixin],
  data() {
    return {
      selectedItemIndex: 0,
    };
  },
  computed: {
    dropdownItems() {
      return [
        {
          id: '7',
          label: this.translate('last7Days'),
        },
        {
          id: '14',
          label: this.translate('last14Days'),
        },
        {
          id: '30',
          label: this.translate('last30Days'),
        },
      ];
    },
    timeperiodStartDate() {
      return convertTimestampToDate(this.startDate);
    },
    timeperiodEndDate() {
      return convertTimestampToDate(this.endDate);
    },
    tasksAddedTooltip() {
      return `${this.translate('cardsAddedBetween')} ${this.timeperiodStartDate}
      ${this.translate('and')} ${this.timeperiodEndDate}. ${this.translate('deletedCards')}.`;
    },
    tasksFinishedTooltip() {
      return `${this.translate('cardsAddedTo')} ${this.completedListStatus()}
      ${this.translate('between')} ${this.timeperiodStartDate}
      ${this.translate('and')} ${this.timeperiodEndDate}. ${this.translate('deletedCards')}.`;
    },
    currentBoard() {
      return this.$store.getters.currentBoard || {};
    },
    reportInfoData() {
      return this.$store.getters.getReportInfoData;
    },
    netChange() {
      const result = this.addedTasks - this.finishedTasks;
      if (result > 0) {
        return `+${result}`;
      }
      return result;
    },
    boardReport() {
      return this.reportInfoData[this.currentBoard.id];
    },
    addedTasks() {
      const [added] = this.boardReport?.stats || [];
      return added?.value || 0;
    },
    finishedTasks() {
      const [, finished] = this.boardReport?.stats || [];
      return finished?.value || 0;
    },
    netChangeTooltip() {
      switch (true) {
        case this.netChange === -1:
          return this.translate('oneMoreCardFinishedThanAdded');
        case this.netChangeValue === 1:
          return this.translate('oneMoreCardAddedThanFinished');
        case this.netChange > 0:
          return this.translate('moreCardsAddedThanFinished', { number: this.netChangeValue });
        case this.netChange === 0:
          return this.translate('sameNumberOfCardsAddedAndFinished');
        case this.netChange < 0:
          return this.translate('moreCardsFinishedThanAdded', { number: this.netChangeValue });
        default:
          return '';
      }
    },
    netChangeValue() {
      const netChangeNumber = Number(this.netChange);
      return Math.abs(netChangeNumber);
    },
  },
  mounted() {
    const savedItem = localStorage.getItem(`${this.$store.getters.getTeamID}-timeperiodOptions`);
    if (savedItem) {
      this.$store.dispatch('setTimeperoidOptions', savedItem);
    }
    const itemOption = this.$store.getters.getTimeperoidOptions;
    const selectedItem = this.dropdownItems.filter((item) => item.id === itemOption);
    this.selectedItemIndex = this.dropdownItems.indexOf(...selectedItem);
    const defaultItem = this.dropdownItems[0];
    this.setDropdownItem(defaultItem, 0);
    this.getLastNDays(7);
    window.addEventListener('popstate', this.handlePopstate);
  },

  unmounted() {
    window.removeEventListener('popstate', this.handlePopstate);
  },

  methods: {
    completedListStatus() {
      const currentBoardLists = this.currentBoard?.lists || [];
      const completedListTitles = currentBoardLists
        .filter((list) => list.behavior === 'completed' || list.behavior === 'cancelled')
        .map((list) => `"${list.title}"`);
      const lastTitle = completedListTitles.pop();
      return completedListTitles.length > 0
        ? `${completedListTitles.join(', ')}
      ${this.translate('and')} ${lastTitle}`
        : lastTitle;
    },
    setDropdownItem(item, index) {
      if (index !== -1 && index !== this.selectedItemIndex) {
        this.selectedItemIndex = index;
        this.$store.dispatch('setTimeperoidOptions', item.id);
        localStorage.setItem(`${this.$store.getters.getTeamID}-timeperiodOptions`, item.id);
      }
    },
    handleItemClick(item, index) {
      this.setDropdownItem(item, index);
      this.getLastNDays(item.id);
      this.fetchReportData();
    },

    handlePopstate() {
      this.$refs.dropdown.closeDropdown();
    },
  },
};
</script>

<style lang="scss">
.last-n-days-button {
  .dropdown-wrap {
    @apply flex #{!important};

    .dropdown-button {
      @apply bg-transparent
      pl-[46px];
    }

    .dropdown-opened {
      @apply border-none
      shadow-none;
    }

    .dropdown-closed {
      @apply border-none
      shadow-none;
    }
  }
}
</style>
